/**
 * Cache tags of the catalogue data (product search, counts, product and article
 * details), and how a flush request is turned into them.
 *
 * A tag is set when a request is made, so it can only name what the caller
 * knows then: the country, the language, a category or a product code. A flush
 * of several tags removes the union of their entries; a tag per combination
 * ({@link getCategoryLocaleCacheTag}) is what makes an intersection possible.
 *
 * All tags are lower case, so a flush finds an entry however its parts were
 * written.
 */

/** Everything the catalogue cached. Not the API access token, which has its own tags. */
export const CATALOGUE_CACHE_TAG = "scott-b2c-catalogue";
/** Search pages and counts only. */
export const PRODUCT_SEARCH_CACHE_TAG = "scott-b2c-product-search";
/** Product, article and PDP details only. */
export const PRODUCT_DETAIL_CACHE_TAG = "scott-b2c-product-detail";

// Next drops a tag longer than this (with a warning), so it is never added.
const MAX_CACHE_TAG_LENGTH = 256;

const normalize = (value: string): string => value.trim().toLowerCase();

/** All entries of one country (base site), in every language. */
export function getSiteCacheTag(baseSiteId: string): string {
  return `scott-b2c-site:${normalize(baseSiteId)}`;
}

/** All entries of one country and language. */
export function getLocaleCacheTag(
  baseSiteId: string,
  language: string | undefined
): string {
  return `scott-b2c-locale:${normalize(baseSiteId)}:${normalize(language ?? "") || "default"}`;
}

/** The search pages and counts of one category, in every locale. */
export function getCategoryCacheTag(categoryId: string): string {
  return `scott-b2c-category:${normalize(categoryId)}`;
}

/** The search pages and counts of one category in one locale. */
export function getCategoryLocaleCacheTag(
  categoryId: string,
  baseSiteId: string,
  language: string | undefined
): string {
  return `scott-b2c-category-locale:${normalize(categoryId)}:${normalize(baseSiteId)}:${normalize(language ?? "") || "default"}`;
}

/** The details of one product and its articles, in every locale. */
export function getProductCacheTag(productCode: string): string {
  return `scott-b2c-product:${normalize(productCode)}`;
}

function withinTagLimit(tags: string[]): string[] {
  return tags.filter((tag) => tag.length <= MAX_CACHE_TAG_LENGTH);
}

/** Tags of a listing's search pages and its count. */
export function getListingCacheTags(params: {
  baseSiteId: string;
  language?: string;
  categoryId?: string;
}): string[] {
  const hasCategory = Boolean(params.categoryId?.trim());

  return withinTagLimit([
    CATALOGUE_CACHE_TAG,
    PRODUCT_SEARCH_CACHE_TAG,
    getSiteCacheTag(params.baseSiteId),
    getLocaleCacheTag(params.baseSiteId, params.language),
    ...(hasCategory && params.categoryId
      ? [
          getCategoryCacheTag(params.categoryId),
          getCategoryLocaleCacheTag(
            params.categoryId,
            params.baseSiteId,
            params.language
          ),
        ]
      : []),
  ]);
}

/**
 * Tags of a product's, an article's or a PDP's detail. For an article,
 * `productCode` is its base product, so flushing the product reaches all its
 * articles.
 */
export function getDetailCacheTags(params: {
  baseSiteId: string;
  language?: string;
  productCode?: string;
}): string[] {
  const hasProduct = Boolean(params.productCode?.trim());

  return withinTagLimit([
    CATALOGUE_CACHE_TAG,
    PRODUCT_DETAIL_CACHE_TAG,
    getSiteCacheTag(params.baseSiteId),
    getLocaleCacheTag(params.baseSiteId, params.language),
    ...(hasProduct && params.productCode
      ? [getProductCacheTag(params.productCode)]
      : []),
  ]);
}

const FIXED_TAGS = new Set([
  CATALOGUE_CACHE_TAG,
  PRODUCT_SEARCH_CACHE_TAG,
  PRODUCT_DETAIL_CACHE_TAG,
]);
const TAG_PREFIXES = [
  "scott-b2c-site:",
  "scott-b2c-locale:",
  "scott-b2c-category:",
  "scott-b2c-category-locale:",
  "scott-b2c-product:",
];

/** Whether `tag` is a catalogue tag, which is all the flush endpoint accepts. */
export function isCatalogueCacheTag(tag: unknown): tag is string {
  if (typeof tag !== "string" || tag.length > MAX_CACHE_TAG_LENGTH) {
    return false;
  }

  return (
    FIXED_TAGS.has(tag) ||
    (TAG_PREFIXES.some(
      (prefix) => tag.startsWith(prefix) && tag.length > prefix.length
    ) &&
      /^[a-z0-9_.\-/:]+$/.test(tag))
  );
}

export type CatalogueFlushRequest = {
  /** `all`, `search` or `detail`: everything of that kind, in every locale. */
  scope?: unknown;
  categoryId?: unknown;
  baseSiteId?: unknown;
  language?: unknown;
  productCode?: unknown;
  /** Catalogue tags as they are, for what the fields above cannot say. */
  tags?: unknown;
};

export type CatalogueFlushResolution =
  | { ok: true; tags: string[] }
  | { ok: false; error: string };

const SCOPE_TAGS: Record<string, string> = {
  all: CATALOGUE_CACHE_TAG,
  detail: PRODUCT_DETAIL_CACHE_TAG,
  search: PRODUCT_SEARCH_CACHE_TAG,
};
const MAX_RAW_TAGS = 20;
const ID_PATTERN = /^[\w.\-/]+$/;

const fail = (error: string): CatalogueFlushResolution => ({
  error,
  ok: false,
});

function readId(value: unknown, field: string): string | undefined | Error {
  if (value === undefined) {
    return;
  }

  if (typeof value !== "string" || !ID_PATTERN.test(value.trim())) {
    return new Error(
      `"${field}" must be a non-empty string without ":" or spaces`
    );
  }

  return value.trim();
}

const FLUSH_FIELDS = [
  "scope",
  "categoryId",
  "baseSiteId",
  "language",
  "productCode",
  "tags",
] as const;

function resolveRawTags(tags: unknown): CatalogueFlushResolution {
  const isValid =
    Array.isArray(tags) &&
    tags.length > 0 &&
    tags.length <= MAX_RAW_TAGS &&
    tags.every(isCatalogueCacheTag);

  return isValid
    ? { ok: true, tags }
    : fail(
        `"tags" must hold 1 to ${MAX_RAW_TAGS} lower case catalogue tags, such as scott-b2c-catalogue`
      );
}

function resolveScope(scope: unknown): CatalogueFlushResolution {
  const tag = typeof scope === "string" ? SCOPE_TAGS[scope] : undefined;

  return tag
    ? { ok: true, tags: [tag] }
    : fail('"scope" must be one of: all, search, detail');
}

function resolveCategory(
  categoryId: string,
  baseSiteId: string | undefined,
  language: string | undefined
): CatalogueFlushResolution {
  if (baseSiteId && language) {
    return {
      ok: true,
      tags: [getCategoryLocaleCacheTag(categoryId, baseSiteId, language)],
    };
  }

  return baseSiteId || language
    ? fail(
        'A category in one locale needs "categoryId", "baseSiteId" and "language"; for the whole category send "categoryId" alone'
      )
    : { ok: true, tags: [getCategoryCacheTag(categoryId)] };
}

function resolveLocale(
  baseSiteId: string | undefined,
  language: string | undefined
): CatalogueFlushResolution {
  if (!baseSiteId) {
    return fail("Name what to flush");
  }

  return {
    ok: true,
    tags: [
      language
        ? getLocaleCacheTag(baseSiteId, language)
        : getSiteCacheTag(baseSiteId),
    ],
  };
}

function resolveByIds(
  request: CatalogueFlushRequest,
  givenCount: number
): CatalogueFlushResolution {
  const categoryId = readId(request.categoryId, "categoryId");
  const baseSiteId = readId(request.baseSiteId, "baseSiteId");
  const language = readId(request.language, "language");
  const productCode = readId(request.productCode, "productCode");
  const invalid = [categoryId, baseSiteId, language, productCode].find(
    (value): value is Error => value instanceof Error
  );

  if (invalid) {
    return fail(invalid.message);
  }

  if (typeof productCode === "string") {
    return givenCount > 1
      ? fail('"productCode" cannot be combined with other fields')
      : { ok: true, tags: [getProductCacheTag(productCode)] };
  }

  // None of the ids is an Error here, so each is a string or undefined.
  const ids = { baseSiteId, categoryId, language } as Record<
    "baseSiteId" | "categoryId" | "language",
    string | undefined
  >;

  if (ids.language && !ids.baseSiteId) {
    return fail(
      '"language" needs "baseSiteId", because languages differ per country'
    );
  }

  return ids.categoryId
    ? resolveCategory(ids.categoryId, ids.baseSiteId, ids.language)
    : resolveLocale(ids.baseSiteId, ids.language);
}

/**
 * Turns a flush request into the one tag it means, or says why it is not
 * clear. Nothing is guessed: a request that names too little or an
 * unsupported combination is refused rather than flushing something larger.
 *
 * | request                                | flushes                                   |
 * | -------------------------------------- | ----------------------------------------- |
 * | `scope`                                | everything / all search / all details     |
 * | `baseSiteId`                           | one country                               |
 * | `baseSiteId` + `language`              | one country and language                  |
 * | `categoryId`                           | one category, in every locale             |
 * | `categoryId` + `baseSiteId` + `language` | one category in one locale              |
 * | `productCode`                          | one product's details, in every locale    |
 * | `tags`                                 | the given catalogue tags                  |
 */
export function resolveCatalogueFlush(
  request: CatalogueFlushRequest
): CatalogueFlushResolution {
  const given = FLUSH_FIELDS.filter((field) => request[field] !== undefined);

  if (given.length === 0) {
    return fail(
      "Name what to flush: scope, baseSiteId, language, categoryId, productCode or tags"
    );
  }

  if (request.tags !== undefined) {
    return given.length > 1
      ? fail('"tags" cannot be combined with other fields')
      : resolveRawTags(request.tags);
  }

  if (request.scope !== undefined) {
    const scope = resolveScope(request.scope);

    return scope.ok && given.length > 1
      ? fail('"scope" cannot be combined with other fields')
      : scope;
  }

  return resolveByIds(request, given.length);
}
